"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { type ComponentPropsWithoutRef, forwardRef } from "react";
import { navItemHref } from "@/lib/contentful/navItem";
import { newTabProps } from "@/lib/contentful/ui";

type Props = Omit<ComponentPropsWithoutRef<"a">, "href"> & {
  /** Raw CMS destination — a slug, rooted path, or full URL. */
  destination: string | undefined;
  isNewTab?: boolean;
  /** Fires on click with the resolved href (use for analytics, closing menus). */
  onNavigate?: (href: string) => void;
};

/**
 * Shared link primitive for CMS-authored destinations, used by both the nav
 * menu and the footer. A bare slug resolves to an internal route against the
 * current root domain, a full URL passes through verbatim, and an unsafe or
 * empty destination renders as plain text instead of a dead anchor. Forwards
 * its ref and arbitrary anchor props so it can sit inside a Radix
 * `NavigationMenuLink asChild` slot.
 */
export const CmsLink = forwardRef<HTMLAnchorElement, Props>(function CmsLink(
  { destination, isNewTab, className, children, onNavigate, onClick, ...rest },
  ref,
) {
  const pathname = usePathname();
  // Resolve once: slug → internal route, full URL verbatim, unsafe scheme → "".
  const href = navItemHref(destination);

  // A dropped/empty destination would be a dead link to the current page, so
  // render the label as plain text instead of a navigable anchor.
  if (!href) {
    return <span className={className}>{children}</span>;
  }

  return (
    <Link
      ref={ref}
      href={href}
      className={className}
      {...rest}
      {...newTabProps(isNewTab)}
      aria-current={pathname === href ? "page" : undefined}
      onClick={e => {
        onClick?.(e);
        onNavigate?.(href);
      }}
    >
      {children}
    </Link>
  );
});
